import { useParseFilter, useTable, useTableRequest } from '@dfl/mui-admin-layout';
import { useQuery } from '@tanstack/react-query';
import {
  REPORT_PRODUCT_STOCK_ACTIVITY_HISTOGRAM,
  REPORT_PRODUCT_STOCK_ACTIVITY_REDUCTION,
  REPORT_PRODUCT_STOCK_ACTIVITY_TOP_USER,
  REPORT_PRODUCT_STOCK_SUMMARY,
} from 'modules/reports/constants/query-keys';
import { useHeaderFilterContext } from 'modules/reports/contexts/HeaderFilterContext';
import { StockInventoryReportService } from 'modules/reports/services';
import { useMemo } from 'react';
import { stringToRange } from 'modules/dashboard/components/DateRangeFilter/options';

export const useInventoryStockSummary = (productId: string) => {
  const { fetch, queryKey } = useTableRequest((params, config) =>
    StockInventoryReportService.inventoryStockSummary(productId, {}, config),
  );

  return useQuery({
    queryKey: [REPORT_PRODUCT_STOCK_SUMMARY, queryKey, productId],
    queryFn: fetch,
    enabled: !!productId,
  });
};

export const useInventoryStockHistogram = (productId: string) => {
  const { interval } = useHeaderFilterContext();
  const { filters: tableFilters } = useTable();
  const urlFilterObj = useParseFilter(tableFilters);
  const startDate = useMemo(() => {
    const range = stringToRange(urlFilterObj?.createdAt as string);
    return range?.[0];
  }, [urlFilterObj?.createdAt]);
  const isEnabled = useMemo(() => [!!productId, !!interval].every(Boolean), [productId, interval]);
  const { fetch, queryKey } = useTableRequest(({ filters }, config) =>
    StockInventoryReportService.inventoryActivityStockHistogram(productId, { filters, interval, startDate }, config),
  );

  return useQuery({
    queryKey: [REPORT_PRODUCT_STOCK_ACTIVITY_HISTOGRAM, queryKey?.filters, productId, interval, startDate],
    queryFn: fetch,
    enabled: isEnabled,
  });
};

export const useInventoryStockTopUser = (productId: string) => {
  const { interval } = useHeaderFilterContext();
  const isEnabled = useMemo(() => [!!productId, !!interval].every(Boolean), [productId, interval]);
  const { fetch, queryKey } = useTableRequest(({ filters }, config) =>
    StockInventoryReportService.inventoryActivityStockTopUser(productId, { filters, interval }, config),
  );

  return useQuery({
    queryKey: [REPORT_PRODUCT_STOCK_ACTIVITY_TOP_USER, queryKey?.filters, productId, interval],
    queryFn: fetch,
    enabled: isEnabled,
  });
};

export const useInventoryStockReduction = (productId: string) => {
  const { interval } = useHeaderFilterContext();
  const isEnabled = useMemo(() => [!!productId, !!interval].every(Boolean), [productId, interval]);
  const { fetch, queryKey } = useTableRequest(({ filters }, config) =>
    StockInventoryReportService.inventoryActivityStockReduction(productId, { filters, interval }, config),
  );

  return useQuery({
    queryKey: [REPORT_PRODUCT_STOCK_ACTIVITY_REDUCTION, queryKey?.filters, productId, interval],
    queryFn: fetch,
    enabled: isEnabled,
  });
};

export const useProductSalesHistogram = (productId: string) => {
  const { interval } = useHeaderFilterContext();
  const isEnabled = useMemo(() => [!!productId, !!interval].every(Boolean), [productId, interval]);
  const { fetch, queryKey } = useTableRequest(({ filters }, config) =>
    StockInventoryReportService.inventoryActivityStockReduction(productId, { filters, interval }, config),
  );

  return useQuery({
    queryKey: [REPORT_PRODUCT_STOCK_ACTIVITY_REDUCTION, queryKey?.filters, productId, interval],
    queryFn: fetch,
    enabled: isEnabled,
  });
};
